import { Text } from "@dropbox/dig-components/typography";
import { Box, Split, Stack } from "@dropbox/dig-foundations";
import { PersonMetadata } from "@mirage/feed/components/ActivityListItem/ExpandedActivityListItem/PersonMetadata";
import { SearchResultIcon } from "@mirage/shared/icons";
import i18n from "@mirage/translations";
import { format } from "date-fns";
import styles from "./MultimediaSearchResults.module.css";

import { Button } from "@dropbox/dig-components/buttons";
import { callApiV2 } from "@mirage/service-dbx-api";
import type { SearchResult } from "@mirage/shared/search/search-result";
import { useEffect, useState } from "react";
import {
  IMAGE_METADATA_KEY_TO_LABELS,
  IMAGE_METADATA_ORDER_COMPARATOR,
  ImageMetadataEntry,
} from "./utils/multimediaExifDataConstants";

export const MediaResultExtendedMetadata = ({
  result,
  hideTitle,
}: {
  result: SearchResult;
  hideTitle?: boolean;
}) => {
  const [imageMetadata, setImageMetadata] = useState<ImageMetadataEntry[]>([]);
  const [showMore, setShowMore] = useState(false);

  const retrieveMetadata = async (incomingUuid: string) => {
    const metadata = await callApiV2("dcsGetMetadataById", {
      ids: [incomingUuid],
      include_media_metadata: true,
    });
    if (metadata.results) {
      const imageMetadata = metadata.results[0]?.media_metadata?.image_metadata;
      if (imageMetadata) {
        const metadataEntries: ImageMetadataEntry[] = [];
        Object.entries(imageMetadata).forEach(([key, value]) => {
          if (typeof value === "string" || typeof value === "number") {
            const metadataValue = IMAGE_METADATA_KEY_TO_LABELS[key];
            if (metadataValue) {
              metadataEntries.push({
                key,
                label: metadataValue.label,
                value: metadataValue.renderer
                  ? metadataValue.renderer(value)
                  : value,
              });
            }
            metadataEntries.sort(IMAGE_METADATA_ORDER_COMPARATOR);
          }
        });
        setImageMetadata(metadataEntries);
      }
    }
  };

  useEffect(() => {
    setShowMore(false);
    setImageMetadata([]);
    retrieveMetadata(result.uuid);
  }, [result.uuid]);

  return (
    <>
      {!hideTitle && (
        <div className={styles.detailsHeader}>
          <Text size="large" isBold>
            {i18n.t("details")}
          </Text>
        </div>
      )}
      <Stack gap="12px">
        <MetadataEntry
          label="Source"
          value={
            <Split gap="8px">
              <Split.Item>
                <SearchResultIcon size={20} result={result} />
              </Split.Item>
              <Split.Item>
                <span>{result.connectorInfo.displayName}</span>
              </Split.Item>
            </Split>
          }
        />
        {result.fileTypeInfo?.displayName ? (
          <MetadataEntry
            label="Type"
            value={result.fileTypeInfo?.displayName}
          />
        ) : null}
        {result.lastModifier?.displayName ? (
          <>
            <MetadataEntry
              label="Updated by"
              value={<PersonMetadata person={result.lastModifier} />}
            />
          </>
        ) : undefined}
        {result.providerUpdateAtMs ? (
          <MetadataEntry
            label="Last updated"
            value={format(
              new Date(result.providerUpdateAtMs),
              "M/dd/yyyy hh:mm a",
            )}
          />
        ) : undefined}
        {result.creator?.displayName ? (
          <>
            <MetadataEntry
              label="Created by"
              value={<PersonMetadata person={result.creator} />}
            />
            {result.providerCreatedAtMs && (
              <MetadataEntry
                label="Date created"
                value={format(
                  new Date(result.providerCreatedAtMs),
                  "M/dd/yyyy hh:mm a",
                )}
              />
            )}
          </>
        ) : undefined}
        {result.media?.xPixels && result.media?.yPixels ? (
          <MetadataEntry
            label="Dimensions"
            value={`${result.media.xPixels} x ${result.media.yPixels}`}
          />
        ) : undefined}
        {result.media?.size ? (
          <MetadataEntry
            label="Size"
            value={`${Math.round(result.media.size / 1024)} KB`}
          />
        ) : undefined}
        {imageMetadata
          .slice(0, showMore ? imageMetadata.length : 4)
          .map((metadata) => {
            return (
              <MetadataEntry
                key={metadata.key}
                label={metadata.label}
                value={metadata.value}
              />
            );
          })}
        {imageMetadata.length > 4 ? (
          <Button
            variant="transparent"
            size="small"
            onClick={() => setShowMore(!showMore)}
            tone="neutral"
          >
            Show {showMore ? "less" : "more"}
          </Button>
        ) : null}
      </Stack>
    </>
  );
};

const MetadataEntry = (props: { label: string; value: React.ReactNode }) => {
  return (
    <Box width="100%" className={styles.metadataEntry}>
      <Split width="100%">
        <Split.Item width="fill">
          <Text variant="label" color="subtle">
            {props.label}
          </Text>
        </Split.Item>
        <Split.Item width="fill">
          <Text>{props.value}</Text>
        </Split.Item>
      </Split>
    </Box>
  );
};
